export const CATEGORY_TRANSITION_COOLDOWN_MS = 350;
export const CATEGORY_SWIPE_MIN_DISTANCE_PX = 50;
export const CATEGORY_SWIPE_CLICK_SUPPRESSION_MS = 600;
export const CATEGORY_DRAG_MAX_OFFSET_PX = 32;

export const createCategorySwipeTracker = () => {
  let startPoint = null;
  let directionLock = null;

  const cancel = () => {
    startPoint = null;
    directionLock = null;
  };

  const start = (point) => {
    startPoint = point;
    directionLock = null;
  };

  const move = (point) => {
    if (!startPoint || directionLock === 'vertical') return directionLock;

    const horizontalDistance = Math.abs(point.x - startPoint.x);
    const verticalDistance = Math.abs(point.y - startPoint.y);
    if (Math.max(horizontalDistance, verticalDistance) < 12) return directionLock;

    if (horizontalDistance < verticalDistance * 1.25) directionLock = 'vertical';
    else directionLock = 'horizontal';
    return directionLock;
  };

  const drag = (point) => {
    const axis = move(point);
    const distance = axis === 'horizontal' ? (point.x - startPoint.x) * 0.45 : 0;
    return {
      axis,
      offset: Math.max(-CATEGORY_DRAG_MAX_OFFSET_PX, Math.min(CATEGORY_DRAG_MAX_OFFSET_PX, distance))
    };
  };

  const end = (point) => {
    if (!startPoint) return null;
    move(point);

    const horizontalDistance = point.x - startPoint.x;
    const verticalDistance = Math.abs(point.y - startPoint.y);
    const isSwipe =
      directionLock === 'horizontal' &&
      Math.abs(horizontalDistance) >= CATEGORY_SWIPE_MIN_DISTANCE_PX &&
      Math.abs(horizontalDistance) >= verticalDistance * 1.25;

    cancel();
    if (!isSwipe) return null;
    return horizontalDistance < 0 ? 'next' : 'previous';
  };

  return { start, move, drag, end, cancel };
};

export const createCategoryDragFeedback = (
  getElements,
  {
    requestFrame = (callback) => window.requestAnimationFrame(callback),
    cancelFrame = (frameId) => window.cancelAnimationFrame(frameId)
  } = {}
) => {
  let frameId = null;
  let desiredOffset = 0;

  const apply = (offset, duration = 0) => {
    for (const element of getElements().filter(Boolean)) {
      element.style.transition = duration ? `transform ${duration}ms ease-out` : 'none';
      element.style.transform = offset ? `translate3d(${offset}px, 0, 0)` : '';
    }
  };

  const cancelPendingFrame = () => {
    if (frameId === null) return false;
    cancelFrame(frameId);
    frameId = null;
    return true;
  };

  const drag = (offset) => {
    desiredOffset = offset;
    if (frameId !== null) return;
    frameId = requestFrame(() => {
      frameId = null;
      apply(desiredOffset);
    });
  };

  const settle = (offset, duration = 150) => {
    if (cancelPendingFrame()) {
      apply(desiredOffset);
      frameId = requestFrame(() => {
        frameId = null;
        apply(offset, duration);
      });
    } else {
      apply(offset, duration);
    }
    desiredOffset = offset;
  };

  const clear = () => {
    cancelPendingFrame();
    desiredOffset = 0;
    for (const element of getElements().filter(Boolean)) {
      element.style.transition = '';
      element.style.transform = '';
    }
  };

  return {
    drag,
    reset: (duration = 150) => settle(0, duration),
    commit: (direction) => settle(direction === 'next' ? -38 : 38, 110),
    clear
  };
};

export const getAdjacentCategoryNo = (categories, currentCategoryNo, direction, categoryKey = 'productCategoryNo') => {
  const currentIndex = categories.findIndex((category) => category[categoryKey] === currentCategoryNo);
  if (currentIndex < 0) return undefined;

  const adjacentIndex = currentIndex + (direction === 'next' ? 1 : -1);
  return categories[adjacentIndex]?.[categoryKey];
};

export const getCategoryTransitionDirection = (
  categories,
  currentCategoryNo,
  nextCategoryNo,
  categoryKey = 'productCategoryNo'
) => {
  const currentIndex = categories.findIndex((category) => category[categoryKey] === currentCategoryNo);
  const nextIndex = categories.findIndex((category) => category[categoryKey] === nextCategoryNo);
  if (currentIndex < 0 || nextIndex < 0 || currentIndex === nextIndex) return null;
  return nextIndex > currentIndex ? 'next' : 'previous';
};

export const createSwipeClickSuppressor = (
  { durationMs = CATEGORY_SWIPE_CLICK_SUPPRESSION_MS, now = Date.now } = {}
) => {
  let suppressedUntil = 0;

  return {
    suppress: () => {
      suppressedUntil = now() + durationMs;
    },
    shouldSuppress: () => now() < suppressedUntil
  };
};

export const createCategoryTransitionGuard = (
  selectCategory,
  { cooldownMs = CATEGORY_TRANSITION_COOLDOWN_MS, now = Date.now } = {}
) => {
  let isPending = false;
  let lastCompletedAt = Number.NEGATIVE_INFINITY;

  return async (categoryNo) => {
    const startedAt = now();
    if (isPending || startedAt - lastCompletedAt < cooldownMs) return false;

    isPending = true;
    let completed = false;

    try {
      await selectCategory(categoryNo);
      completed = true;
      return true;
    } finally {
      isPending = false;
      if (completed) lastCompletedAt = now();
    }
  };
};
